import { combineReducers, createStore, applyMiddleware } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import thunk from 'redux-thunk';

import { byrConfigReducer } from './BuildYourRoof/BYRConnectors';
import { byrEditorReducer } from './BuildYourRoof/CustomizeConnectors';
import { featureFlagReducer } from './FeatureFlag/FeatureFlagConnectors';
import { genericLocatorReducer } from './OC/GenericLocator/GenericLocatorConnectors';
import { cmsMiddleware } from './PageBuilder/redux/connector';
import { celluloseCalculatorReducer } from './ProPink/CelluloseConnectors';
import { fiberglassCalculatorReducer } from './ProPink/FiberglassConnectors';
import { sprayfoamReducer } from './ProPink/SprayfoamConnectors';
import { productsCompatibilityReducer } from './Roofing/ProductsCompatibility';
import { shinglesFavoritesReducer } from './Roofing/Shingles/Favorites/FavoritesConnectors';
import { shinglesRecentlyViewedReducer } from './Roofing/Shingles/Favorites/RecentlyViewedConnectors';
import subscriptions from './Subscriptions';
import { videoMetadataReducer } from './VideoMetadata/VideoMetadataConnector';
import { locationReducer } from './location/LocationConnectors';
import { enclosureReducer } from './wallbuilder/connector';
import styleBoardReducer from '../redux/reducers/StyleBoardReducer';
import URLSearchParamReducer from '../redux/reducers/URLSearchParamReducer';
import URLSearchParamSubscription from '../redux/subscriptions/URLSearchParamSubscription';

export const KEY = 'root';

export const REDUX_PERSIST_PREFIX = 'persist:';

const nullStorage = {
  setItem() {
    return Promise.resolve();
  },
  getItem() {
    return Promise.resolve();
  },
  removeItem() {
    return Promise.resolve();
  },
};

const persistConfig = {
  key: KEY,
  storage: typeof window !== 'undefined' ? storage : nullStorage,
  whitelist: ['shinglesFavorites', 'shinglesRecentlyViewed'],
};

// Define the static reducers that will always be present in the application
const staticReducers = {
  byrConfig: byrConfigReducer,
  byrEditor: byrEditorReducer,
  productsCompatibility: productsCompatibilityReducer,
  shinglesFavorites: shinglesFavoritesReducer,
  shinglesRecentlyViewed: shinglesRecentlyViewedReducer,
  fiberglassCalculator: fiberglassCalculatorReducer,
  celluloseCalculator: celluloseCalculatorReducer,
  sprayfoamCalculator: sprayfoamReducer,
  enclosure: enclosureReducer,
  location: locationReducer,
  urlParams: URLSearchParamReducer,
  genericLocator: genericLocatorReducer,
  videoMetadata: videoMetadataReducer,
  featureFlag: featureFlagReducer,
  styleBoard: styleBoardReducer,
};

// Create a function to create the root reducer with both static and async reducers
function createReducer(asyncReducers = {}) {
  const combinedReducers = combineReducers({
    ...staticReducers,
    ...asyncReducers,
  });

  // Wrap the combined reducers to maintain compatibility with redux-persist
  return persistReducer(persistConfig, combinedReducers);
}

export function subscribe(store) {
  const unsubscribeDefaultSubscriptions = subscriptions(store);
  const unsubscribeUrlSubscriptions = URLSearchParamSubscription(store);

  let subscribed = true;
  const unsubscribeAll = () => {
    // prevent multiple calls to unsubscribe, which can cause errors
    if (!subscribed) return;

    unsubscribeDefaultSubscriptions();
    unsubscribeUrlSubscriptions();
    subscribed = false;
  };
  // eslint-disable-next-line no-param-reassign
  store.unsubscribeAll = unsubscribeAll;
  return unsubscribeAll;
}

export function unsubscribe(store) {
  if (typeof store?.unsubscribeAll === 'function') store.unsubscribeAll();
}

const commonStore = createStore(createReducer(), composeWithDevTools(applyMiddleware(thunk, cmsMiddleware)));

// Add an object to keep track of the registered async reducers
commonStore.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
commonStore.injectReducer = (key, asyncReducer, initialState = {}) => {
  if (!key || commonStore.asyncReducers[key] === asyncReducer) {
    return;
  }

  // Add the reducer to the async reducers
  commonStore.asyncReducers[key] = asyncReducer;

  // Replace the store's reducer with a new one that includes the injected reducer
  commonStore.replaceReducer(createReducer(commonStore.asyncReducers));

  // If we have initialState for this reducer, dispatch an init action
  if (initialState && Object.keys(initialState).length > 0) {
    commonStore.dispatch({ type: `${key}/INIT`, payload: initialState });
  }
};

// Create a function to get a specific reducer's state
commonStore.getReducerState = key => commonStore.getState()[key];

subscribe(commonStore);

export { commonStore as store };

export const reducers = createReducer();

export const persistor = persistStore(commonStore);
